import * as React from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, type createBrowserRouter } from 'react-router-dom';
import { queryClient } from '@/lib/query-client';
import { ThemeProvider } from '@/app/theme/ThemeProvider';
import { AuthProvider } from '@/auth/AuthProvider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { createAppRouter } from '@/app/router';
import { ROUTER_PROVIDER_FUTURE } from '@/app/router-future';

type AppRouter = ReturnType<typeof createBrowserRouter>;

/**
 * Provider stack + router. Order matters: the QueryClient is needed by AuthProvider, the theme by
 * the Toaster. Pass a memory router in tests.
 */
export function App({ router }: { router?: AppRouter }) {
  const [appRouter] = React.useState<AppRouter>(() => router ?? createAppRouter());
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <TooltipProvider delayDuration={300}>
            <RouterProvider router={appRouter} future={ROUTER_PROVIDER_FUTURE} />
            <Toaster />
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
